// The one HTTP client the signed-in pages call. Lanes write their calls in
// their own src/services/api/<area>.js on top of this; nobody adds methods
// to mixins/cloudloop.js.
//
//   apiGet(path, query, opts)   apiPost(path, body, opts)
//   apiPut(path, body, opts)    apiPatch(path, body, opts)
//   apiDelete(path, body, opts)
//
// - Every request sends the stored JWT (services/authHeaders.js) and
//   credentials: "include", and every query value is encoded.
// - The server wraps answers as {status, message, data: {results}}; the
//   client returns `results`. A body with no `data` (the shots harness's
//   catch-all for a missing fixture) returns null: a fixture gap, not data.
// - Errors throw {status, message, body}. A 401 on any call drops the
//   signed-in user, as the router guard would.
// - opts.optional marks an endpoint a server may lack (/sounds, /packs,
//   /activity, /people/recent, /storage/breakdown, /sales): only those turn
//   a 404 or 405 into {comingSoon: true}. Every other 404 is an error.
//
// Payloads one level deeper than `results`:
//   GET /invitations/inbox        the rows are at data.results.results
//   GET /invitations/inbox/count  the count is at data.results.count
import { apiHeaders } from "../authHeaders.js";

export const COMING_SOON = Object.freeze({ comingSoon: true });

function readToken() {
  try {
    return typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem("JWT") : null;
  } catch (e) {
    return null;
  }
}

// "?a=1&b=x%20y": null and undefined are left out, arrays join with commas.
export function queryString(query) {
  if (!query || typeof query !== "object") return "";
  const parts = [];
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    const value = Array.isArray(v) ? v.join(",") : typeof v === "boolean" ? (v ? "1" : "0") : String(v);
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(value)}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

/**
 * A client over `fetchImpl`. `base` is the API origin (VUE_APP_API_URL);
 * `onUnauthorized` runs on any 401.
 */
export function createClient({ base = "", fetchImpl = null, token = readToken, onUnauthorized = null } = {}) {
  const state = { onUnauthorized };

  async function request(method, path, { query = null, body = undefined, optional = false, signal = null } = {}) {
    const f = fetchImpl || (typeof fetch === "function" ? fetch : null);
    if (!f) throw { status: 0, message: "No network", body: null };
    const url = `${base}/api${path.startsWith("/") ? path : `/${path}`}${queryString(query)}`;
    const init = { method, credentials: "include", headers: apiHeaders(token()) };
    if (signal) init.signal = signal;
    if (body !== undefined && method !== "GET") init.body = JSON.stringify(body);
    let res;
    try {
      res = await f(url, init);
    } catch (e) {
      if (e && e.name === "AbortError") throw e;
      throw { status: 0, message: "Couldn't reach cloudloop.", body: null };
    }
    let parsed = null;
    try {
      parsed = await res.json();
    } catch (e) {
      parsed = null;
    }
    if (res.status === 401 && typeof state.onUnauthorized === "function") {
      try {
        state.onUnauthorized();
      } catch (e) {
        /* the guard catches it on the next navigation */
      }
    }
    if (optional && (res.status === 404 || res.status === 405)) return { ...COMING_SOON };
    if (!res.ok) {
      const message = (parsed && (parsed.message || parsed.msg)) || `Request failed (${res.status})`;
      throw { status: res.status, message, body: parsed };
    }
    if (!parsed || typeof parsed !== "object" || !("data" in parsed)) return null;
    const data = parsed.data;
    return data && typeof data === "object" && "results" in data ? data.results : data ?? null;
  }

  return {
    configure(opts = {}) {
      if ("onUnauthorized" in opts) state.onUnauthorized = opts.onUnauthorized;
    },
    request,
    apiGet: (path, query, opts = {}) => request("GET", path, { ...opts, query }),
    apiPost: (path, body, opts = {}) => request("POST", path, { ...opts, body: body ?? {} }),
    apiPut: (path, body, opts = {}) => request("PUT", path, { ...opts, body: body ?? {} }),
    apiPatch: (path, body, opts = {}) => request("PATCH", path, { ...opts, body: body ?? {} }),
    apiDelete: (path, body, opts = {}) => request("DELETE", path, { ...opts, body }),
  };
}

// vue-cli replaces process.env.VUE_APP_* at build time; Node has process.
const base = process.env.VUE_APP_API_URL || "";
const client = createClient({ base });

export const configureClient = client.configure;
export const apiGet = client.apiGet;
export const apiPost = client.apiPost;
export const apiPut = client.apiPut;
export const apiPatch = client.apiPatch;
export const apiDelete = client.apiDelete;
export default client;
